import { z } from 'zod';
import { isoDateTimeSchema } from './common.ts';

/**
 * Autopilot submission runs: one `SubmissionQuote` execution each, scheduled (child of
 * `SubmissionDispatch`) or operator-started (child of `AdhocSubmission`). Built by the API from the
 * decoded workflow history. Only the fields below leave the API; contact details, dates of birth,
 * payment, tax and super numbers, income and health measurements never do.
 */

/** Temporal run ids are UUIDs (v4 or v7). Routes are keyed on them because workflow ids are not URL-safe. */
export const runIdSchema = z
  .string()
  .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/);

export const RUN_STEP_KEYS = [
  'dispatch',
  'resolve',
  'enrich',
  'order',
  'notify',
  'csm',
  'render',
  'post',
  'arbo',
  'issue',
  'crtip',
] as const;
export const runStepKeySchema = z.enum(RUN_STEP_KEYS);
export type RunStepKey = z.infer<typeof runStepKeySchema>;

export const runStepStatusSchema = z.enum([
  'done',
  'warning',
  'skipped',
  'failed',
  'running',
  'pending',
]);
export type RunStepStatus = z.infer<typeof runStepStatusSchema>;

export const RUN_STATUSES = [
  'running',
  'completed',
  'failed',
  'cancelled',
  'terminated',
  'timed_out',
] as const;
export const runStatusSchema = z.enum(RUN_STATUSES);
export type RunStatus = z.infer<typeof runStatusSchema>;

export const RUN_OUTCOMES = [
  'inforced',
  'awaiting_inforce',
  'blocked',
  'referred',
  'medical_ordered',
  'manual_order_needed',
  'uw_review',
  'failed',
  'in_progress',
  'cancelled',
] as const;
export const runOutcomeSchema = z.enum(RUN_OUTCOMES);
export type RunOutcome = z.infer<typeof runOutcomeSchema>;

export const ureDecisionSchema = z.enum([
  'STANDARD',
  'NON_STANDARD',
  'REFER',
  'EVIDENCE_REQUIRED',
  'DECLINE',
  'POSTPONE',
]);
export type UreDecision = z.infer<typeof ureDecisionSchema>;

export const runSourceSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('scheduled'),
    /** Temporal schedule id, e.g. `submission-dispatch-business`. */
    schedule: z.string().nullable(),
    /** The dispatch window the application was listed in (detail only). */
    window: z.object({ start: isoDateTimeSchema, end: isoDateTimeSchema }).nullable(),
  }),
  z.object({
    kind: z.literal('operator'),
    /** Email of the operator who started it (detail only). */
    by: z.string().nullable(),
    note: z.string().nullable(),
  }),
]);
export type RunSource = z.infer<typeof runSourceSchema>;

export const runErrorSchema = z.object({
  type: z.string().nullable(),
  message: z.string(),
});

export const runSummarySchema = z.object({
  runId: runIdSchema,
  workflowId: z.string(),
  quoteNumber: z.string(),
  policyNumber: z.string().nullable(),
  source: runSourceSchema,
  status: runStatusSchema,
  outcome: runOutcomeSchema,
  inforceBlockers: z.array(z.string()),
  /** Medical order reference when one was placed. */
  medicalOrderRef: z.string().nullable(),
  /** Diary task codes on the main policy, in creation order. */
  diaryCodes: z.array(z.string()),
  error: runErrorSchema.nullable(),
  startedAt: isoDateTimeSchema,
  closedAt: isoDateTimeSchema.nullable(),
  /** Runs of this workflow id (more than one after a reset). */
  runCount: z.number().int().min(1),
  /** The workflow's latest run when it is not this one (this run was reset). */
  latestRun: z.object({ runId: z.string(), status: runStatusSchema }).nullable(),
});
export type RunSummary = z.infer<typeof runSummarySchema>;

export const runStepSchema = z.object({
  key: runStepKeySchema,
  status: runStepStatusSchema,
  detail: z.string(),
  startedAt: isoDateTimeSchema.nullable(),
  durationMs: z.number().nullable(),
  attempts: z.number().int().nullable(),
  /** Copied from an earlier run by a reset: it did not run again in this run. */
  carriedOver: z.boolean(),
});
export type RunStep = z.infer<typeof runStepSchema>;

export const runDiaryTaskSchema = z.object({
  code: z.string(),
  name: z.string(),
  status: z.enum(['I', 'C']),
  policyNumber: z.string(),
  linked: z.boolean(),
  assignee: z.string().nullable(),
  /** `YYYY-MM-DD`. */
  followUp: z.string().nullable(),
  note: z.string().nullable(),
  /** ebix diary id once created; null when it was not (yet) created. */
  diaryId: z.string().nullable(),
});
export type RunDiaryTask = z.infer<typeof runDiaryTaskSchema>;

export const runBenefitSchema = z.object({
  code: z.string(),
  name: z.string(),
  sumInsured: z.number().nullable(),
  waitingPeriod: z.string().nullable(),
  benefitPeriod: z.string().nullable(),
  /** Loadings in words, e.g. `Medical +50%`; empty when standard. */
  loadings: z.array(z.string()),
  exclusions: z.array(z.string()),
});
export type RunBenefit = z.infer<typeof runBenefitSchema>;

export const runPolicySchema = z.object({
  policyNumber: z.string(),
  linked: z.boolean(),
  /** `Ordinary`, `Super`, `SMSF` or `Undefined`. */
  ownership: z.string().nullable(),
  annualPremium: z.number().nullable(),
  premiumFrequency: z.string().nullable(),
});
export type RunPolicy = z.infer<typeof runPolicySchema>;

/** One run of a workflow id: the first run, or a reset of an earlier one. */
export const runAttemptSchema = z.object({
  runId: z.string(),
  status: runStatusSchema,
  startedAt: isoDateTimeSchema,
  closedAt: isoDateTimeSchema.nullable(),
  /** The run this one was reset from; null for the first run. */
  resetFromRunId: z.string().nullable(),
});
export type RunAttempt = z.infer<typeof runAttemptSchema>;

/**
 * A point a failed run can be retried from: a Temporal reset to just before this activity was
 * scheduled. Earlier activities are kept (their results are replayed); this one and every later
 * one run again.
 */
export const runRetryPointSchema = z.object({
  activityId: z.string(),
  step: runStepKeySchema,
  /** The failed activity: the usual place to retry from. */
  isDefault: z.boolean(),
  /** Side effects that happen again when retrying from here (already done before the failure). */
  repeats: z.array(z.string()),
});
export type RunRetryPoint = z.infer<typeof runRetryPointSchema>;

export const runRetrySchema = z.object({
  /** Whether this run can be retried at all (the caller's permission is checked separately). */
  eligible: z.boolean(),
  /** Why not, when not eligible. */
  reason: z.string().nullable(),
  /** Retry points, in step order. Empty when not eligible. */
  points: z.array(runRetryPointSchema),
  /** The failure may have partly happened in ebix (e.g. a timeout): check before retrying. */
  checkFirst: z.string().nullable(),
});
export type RunRetry = z.infer<typeof runRetrySchema>;

export const retryRunRequestSchema = z.strictObject({
  fromActivityId: z.string().min(1).max(40),
  reason: z.string().trim().min(3).max(500),
  /** Required when `fromActivityId` is not the default point: earlier side effects run again. */
  confirmRepeats: z.boolean().optional(),
});
export type RetryRunRequest = z.infer<typeof retryRunRequestSchema>;

export const retryRunResponseSchema = z.object({ runId: z.string(), workflowId: z.string() });
export type RetryRunResponse = z.infer<typeof retryRunResponseSchema>;

export const runDetailSchema = runSummarySchema.extend({
  workflowType: z.string(),
  parent: z.object({ workflowId: z.string(), runId: z.string(), type: z.string() }).nullable(),
  productName: z.string().nullable(),
  brand: z.enum(['NEOS', 'Encompass', 'Futura']).nullable(),
  linkedPolicyNumber: z.string().nullable(),
  /** The life insured: name and age next birthday only. */
  customer: z.object({ name: z.string().nullable(), age: z.number().nullable() }),
  adviser: z.object({ name: z.string().nullable(), practice: z.string().nullable() }),
  csm: z.string().nullable(),
  underwriter: z.string().nullable(),
  ureDecision: ureDecisionSchema.nullable(),
  submissionType: z.string().nullable(),
  teleInterview: z.string().nullable(),
  annualPremium: z.number().nullable(),
  benefits: z.array(runBenefitSchema),
  policies: z.array(runPolicySchema),
  /** Other policies the life insured holds; existing business blocks the medical auto-order. */
  existingBusinessCount: z.number().int(),
  medicalOrder: z
    .object({
      provider: z.enum(['UHG', 'EQP']).nullable(),
      tests: z.array(z.string()),
      made: z.boolean(),
      orderId: z.string().nullable(),
      displayId: z.string().nullable(),
    })
    .nullable(),
  steps: z.array(runStepSchema),
  diaryTasks: z.array(runDiaryTaskSchema),
  /** Set when this run was created by resetting an earlier run. */
  resetFrom: z.object({ runId: z.string(), at: isoDateTimeSchema }).nullable(),
  /** Every run of this workflow id, newest first (this one included). */
  runs: z.array(runAttemptSchema),
  retry: runRetrySchema,
});
export type RunDetail = z.infer<typeof runDetailSchema>;

/**
 * `GET /runs/:runId`. With the workflow id the run is read directly (consistent at once); without
 * it the run is found by a visibility search, which lags a second or two behind a new run.
 */
export const runDetailQuerySchema = z.strictObject({
  workflowId: z.string().min(1).max(400).optional(),
});
export type RunDetailQuery = z.infer<typeof runDetailQuerySchema>;

export const RUN_VIEWS = ['all', 'running', 'failed'] as const;
export const RUN_PAGE_SIZES = [10, 25] as const;
/** Visibility has no offset: pages are found by skipping ids, so deep pages are capped. */
export const RUN_LIST_MAX_OFFSET = 2000;

/**
 * A quote number prefix (workflow ids hold the quote number; operator runs also match the number
 * they were started with, which may be a policy number). Restricted so it can go into a
 * visibility query.
 */
export const runSearchSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9-]{1,40}$/);

export const listRunsQuerySchema = z.strictObject({
  view: z.enum(RUN_VIEWS).optional(),
  source: z.enum(['scheduled', 'operator']).optional(),
  q: runSearchSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  size: z.coerce
    .number()
    .int()
    .refine((n) => (RUN_PAGE_SIZES as readonly number[]).includes(n))
    .default(10),
});
export type ListRunsQuery = z.infer<typeof listRunsQuerySchema>;

export const runPageSchema = z.object({
  items: z.array(runSummarySchema),
  /** Matching runs (visibility count). A reset workflow has a row per run. */
  total: z.number().int(),
  page: z.number().int(),
  size: z.number().int(),
});
export type RunPage = z.infer<typeof runPageSchema>;
